import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle, Eye } from "lucide-react";
import toast from "react-hot-toast";
import { authService, employeeService } from "../../services/api";
import { getAuthItem } from "../../utils/authStorage";
import AttachmentPreviewModal from "../../components/Common/AttachmentPreviewModal";

/** Indian FY halves — Apr→Mar; Jan–Mar belong to FY start + 1. */
const FY_H1 = ["April", "May", "June", "July", "August", "September"];
const FY_H2 = ["October", "November", "December", "January", "February", "March"];

const currentYear = new Date().getFullYear();

function fyMonthYear(monthName, fyStartYear) {
  const y =
    monthName === "January" || monthName === "February" || monthName === "March"
      ? fyStartYear + 1
      : fyStartYear;
  return `${monthName}-${y}`;
}

function publishedRows(monthNames, fyStartYear, payslipsByMonth) {
  return monthNames
    .map((month) => {
      const monthYear = fyMonthYear(month, fyStartYear);
      const entry = payslipsByMonth[monthYear];
      if (!entry?.documents?.length) return null;
      return { month, monthYear, entry };
    })
    .filter(Boolean);
}

/** Blob requests return the server's JSON error as a Blob — read its message. */
async function serverErrorMessage(e, fallback) {
  const data = e?.response?.data;
  try {
    const json = data instanceof Blob ? JSON.parse(await data.text()) : data;
    if (json?.message) return String(json.message);
  } catch {
    /* not JSON */
  }
  return fallback;
}

function useEmployeeId() {
  const [employeeId, setEmployeeId] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const userRes = await authService.getCurrentUser();
        const id = userRes?.data?.employeeId ?? userRes?.employeeId ?? null;
        if (!cancelled) setEmployeeId(id);
      } catch {
        if (!cancelled) setEmployeeId(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return employeeId;
}

function currentFyStartYear() {
  const now = new Date();
  return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
}

function usePayslipYearOptions(employeeId) {
  const [employeeProfile, setEmployeeProfile] = useState(null);
  const [companyServiceYear, setCompanyServiceYear] = useState(null);
  const [selectedYear, setSelectedYear] = useState(() => currentFyStartYear());

  useEffect(() => {
    if (!employeeId) {
      setEmployeeProfile(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await employeeService.getEmployee(employeeId);
        const emp = res?.data ?? res;
        if (!cancelled) setEmployeeProfile(emp || null);
      } catch {
        if (!cancelled) setEmployeeProfile(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [employeeId]);

  useEffect(() => {
    let cancelled = false;
    const raw = getAuthItem("companyData");
    if (!raw) {
      setCompanyServiceYear(null);
      return undefined;
    }
    try {
      const company = JSON.parse(raw);
      const serviceOn =
        company?.service_commences_on ?? company?.serviceCommencesOn ?? null;
      const year = serviceOn ? new Date(serviceOn).getFullYear() : null;
      if (!cancelled) setCompanyServiceYear(Number.isNaN(year) ? null : year);
    } catch {
      if (!cancelled) setCompanyServiceYear(null);
    }
    return () => {
      cancelled = true;
    };
  }, []);

  const yearOptions = useMemo(() => {
    const fyEnd = currentFyStartYear();
    const doj =
      employeeProfile?.joiningDate ??
      employeeProfile?.dateOfJoin ??
      employeeProfile?.dateOfJoining ??
      employeeProfile?.serviceCommencedDate ??
      null;
    const dojDate = doj ? new Date(doj) : null;
    const dojFy =
      dojDate && !Number.isNaN(dojDate.getTime())
        ? dojDate.getMonth() >= 3
          ? dojDate.getFullYear()
          : dojDate.getFullYear() - 1
        : null;
    const serviceFy = companyServiceYear ?? null;
    const minYear = [serviceFy, dojFy].filter((y) => y != null).length
      ? Math.max(...[serviceFy, dojFy].filter((y) => y != null))
      : fyEnd;
    const from = Math.min(minYear, fyEnd);
    return Array.from({ length: fyEnd - from + 1 }, (_, i) => fyEnd - i);
  }, [employeeProfile, companyServiceYear]);

  useEffect(() => {
    if (yearOptions.length && !yearOptions.includes(selectedYear)) {
      setSelectedYear(yearOptions[0]);
    }
  }, [yearOptions, selectedYear]);

  return { selectedYear, setSelectedYear, yearOptions };
}

function PayslipHalfCard({ title, rows, onPreview }) {
  return (
    <section className="overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]">
      <div className="border-b border-[hsl(var(--ds-border))] px-4 py-3">
        <h4 className="text-sm font-semibold text-[hsl(var(--ds-fg))]">{title}</h4>
      </div>
      {rows.length === 0 ? (
        <p className="px-4 py-6 text-sm text-[hsl(var(--ds-muted-fg))]">
          No published payslips in this period.
        </p>
      ) : (
        <ul className="divide-y divide-[hsl(var(--ds-border))]">
          {rows.map(({ month, monthYear, entry }) => (
            <li
              key={monthYear}
              className="flex items-center justify-between gap-3 px-4 py-2.5"
            >
              <span className="text-sm font-medium text-[hsl(var(--ds-fg))]">
                {month}
              </span>
              <button
                type="button"
                onClick={() => onPreview(monthYear, entry)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-[11px] text-[hsl(var(--ds-primary))] transition-colors hover:bg-[hsl(var(--ds-muted))]"
                aria-label={`Preview ${month} payslip`}
                title="Preview"
              >
                <Eye size={18} strokeWidth={1.7} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function EmployeePayslipsPanel() {
  const employeeId = useEmployeeId();
  const { selectedYear, setSelectedYear, yearOptions } =
    usePayslipYearOptions(employeeId);
  const [payslipsByMonth, setPayslipsByMonth] = useState({});
  const [loading, setLoading] = useState(false);
  const [previewState, setPreviewState] = useState({
    isOpen: false,
    url: null,
    downloadUrl: null,
    fileName: null,
    blobUrlToRevoke: null,
  });

  /** FY Apr–Mar spans two calendar years — load both. */
  const fetchMyPayslips = useCallback(async (fyStartYear) => {
    setLoading(true);
    try {
      const years = [fyStartYear, fyStartYear + 1];
      const results = await Promise.all(
        years.map((y) => employeeService.getMyPayslipsForYear(y))
      );
      const map = {};
      results.forEach((res) => {
        if (res?.success && Array.isArray(res.data)) {
          res.data.forEach((entry) => {
            map[entry.monthYear] = entry;
          });
        }
      });
      setPayslipsByMonth(map);
    } catch (e) {
      console.error("Error fetching my payslips:", e);
      toast.error("Failed to load payslips");
      setPayslipsByMonth({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMyPayslips(selectedYear);
  }, [selectedYear, fetchMyPayslips]);

  const h1Rows = useMemo(
    () => publishedRows(FY_H1, selectedYear, payslipsByMonth),
    [selectedYear, payslipsByMonth]
  );
  const h2Rows = useMemo(
    () => publishedRows(FY_H2, selectedYear, payslipsByMonth),
    [selectedYear, payslipsByMonth]
  );

  const closePreview = () => {
    if (previewState.blobUrlToRevoke) {
      window.URL.revokeObjectURL(previewState.blobUrlToRevoke);
    }
    setPreviewState({
      isOpen: false,
      url: null,
      downloadUrl: null,
      fileName: null,
      blobUrlToRevoke: null,
    });
  };

  const resolvePayslipType = (entry) =>
    entry?.documents?.find((d) => d.type === "standard")?.type ??
    entry?.documents?.[0]?.type ??
    "standard";

  const fetchPayslipBlob = async (monthYear, entry) => {
    if (!employeeId) {
      toast.error("Unable to identify employee");
      return null;
    }
    const type = resolvePayslipType(entry);
    const data = await employeeService.downloadPayslip(
      employeeId,
      monthYear,
      type
    );
    const blob =
      data instanceof Blob ? data : new Blob([data], { type: "application/pdf" });
    return { blob, fileName: `${monthYear}.pdf` };
  };

  const handleViewPayslip = async (monthYear, entry) => {
    const toastId = toast.loading("Generating payslip PDF… please wait");
    try {
      const result = await fetchPayslipBlob(monthYear, entry);
      if (!result) {
        toast.dismiss(toastId);
        return;
      }
      const url = window.URL.createObjectURL(result.blob);
      setPreviewState({
        isOpen: true,
        url,
        downloadUrl: url,
        fileName: result.fileName,
        blobUrlToRevoke: url,
      });
      toast.success("Payslip ready", { id: toastId });
    } catch (e) {
      console.error("View payslip error:", e);
      toast.error(await serverErrorMessage(e, "Failed to preview payslip PDF"), {
        id: toastId,
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-[hsl(var(--ds-fg))]">
            Payslips
          </h3>
          <p className="mt-0.5 text-sm text-[hsl(var(--ds-muted-fg))]">
            Only months with a published payslip are listed. Open the eye icon to
            preview (download is available in the preview).
          </p>
        </div>
        <label className="block text-sm text-[hsl(var(--ds-muted-fg))]">
          Financial year
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="mt-1 block min-w-[9rem] rounded-[11px] border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2.5 py-1.5 text-sm text-[hsl(var(--ds-fg))]"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}–{String(y + 1).slice(-2)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <div className="text-sm text-[hsl(var(--ds-muted-fg))]">
          Loading payslips…
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <PayslipHalfCard
            title={`April – September ${selectedYear}`}
            rows={h1Rows}
            onPreview={handleViewPayslip}
          />
          <PayslipHalfCard
            title={`October ${selectedYear} – March ${selectedYear + 1}`}
            rows={h2Rows}
            onPreview={handleViewPayslip}
          />
        </div>
      )}

      <AttachmentPreviewModal
        isOpen={previewState.isOpen}
        onClose={closePreview}
        url={previewState.url}
        downloadUrl={previewState.downloadUrl}
        fileName={previewState.fileName}
      />
    </div>
  );
}

export function EmployeeForm16Panel() {
  const [form16Data, setForm16Data] = useState([]);

  useEffect(() => {
    fetch("/api/form16")
      .then((res) => res.json())
      .then((data) => setForm16Data(Array.isArray(data) ? data : []))
      .catch(() => setForm16Data([]));
  }, []);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-[hsl(var(--ds-fg))]">Form-16</h3>
        <p className="mt-0.5 text-sm text-[hsl(var(--ds-muted-fg))]">
          Annual TDS certificate (Part A / Part B) when issued by payroll.
        </p>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {["Part-A", "Part-B"].map((part) => (
          <div
            key={part}
            className="overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
          >
            <div className="border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-4 py-2.5 text-sm font-semibold text-[hsl(var(--ds-fg))]">
              {part}
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]">
                  <th className="px-4 py-2 text-left font-medium">Year</th>
                  <th className="px-4 py-2 text-left font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {(form16Data.length ? form16Data : [{ year: "—" }]).map((yearData) => (
                  <tr key={`${part}-${yearData.year}`} className="border-t border-[hsl(var(--ds-border))]">
                    <td className="px-4 py-2.5">{yearData.year}</td>
                    <td className="px-4 py-2.5">
                      {yearData[part] ? (
                        <CheckCircle className="h-5 w-5 text-[hsl(var(--ds-success))]" />
                      ) : (
                        <span className="text-[hsl(var(--ds-muted-fg))]">N/A</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}
